import { useState, useEffect, useMemo } from 'react';
import Header from './header';
import TaskItemsGroup from './taskItemsGroup';
import { DealProvider, useDeal } from './context';
import { Button, Empty } from '@components';
import { InboxArrowDownIcon } from '@heroicons/react/24/outline';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';
import { arrayMoveImmutable } from 'array-move';
import { ReactSortable } from 'react-sortablejs';
import { sortBy } from 'lodash';
import {
  ArrowUpTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { Skeleton } from '../utils/skeleton';
import { ToolbarOverlay } from './toolbarOverlay';
import { Filters } from './filters';
import { ToolbarComponent } from './toolbar';
import AddModal from './addModal';

function TaskItemsList() {
  const {
    dealId,
    isRefreshing,
    actions = {},
    taskItemsGroups,
    setTaskItemsGroups,
    loading,
    closedGroupIds,
    toggleAllGroups,
    keyword,
    setKeyword,
    setSelectedItemIds,
    selectedItemIds,
    taskItemById,
    searchedTaskItemIds,
    visibleTaskItemIdsForGroup,
    initialLoading,
    currentTaskItemFilter,
    addModalIsOepned,
    setAddModalIsOpened
  } = useDeal();

  const sortedGroups = sortBy(taskItemsGroups, 'position')
    .filter(group => {
      if (currentTaskItemFilter === 'INTERNAL') {
        return !!group.internal;
      } else if (currentTaskItemFilter === 'PUBLIC') {
        return !group.internal;
      }

      return group;
    })
    // Drop groups that render nothing (empty or fully filtered out): a
    // <ReactSortable> child returning null desyncs `list` from the DOM and
    // corrupts drag ordering. Keep `list` 1:1 with the rendered groups.
    .filter(group => visibleTaskItemIdsForGroup(group).length > 0);

  const sortingTrigger = [
    JSON.stringify(sortedGroups.map(g => ({ id: g.id, position: g.position }))),
    currentTaskItemFilter
  ].join('-');

  const totalFilesCount = useMemo(() => {
    const filesCount = Object.values(taskItemById).reduce((acc, taskItem) => {
      if ((selectedItemIds || []).includes(taskItem.id)) {
        acc += Number(taskItem.files_count);
      }
      return acc;
    }, 0);
    return filesCount;
  }, [selectedItemIds]);

  const totalRecipientsCount = useMemo(() => {
    const usersCount = Object.values(taskItemById).reduce((acc, taskItem) => {
      if ((selectedItemIds || []).includes(taskItem.id)) {
        acc += Number(taskItem.viewers.length);
        acc += Number(taskItem.editors.length);
      }
      return acc;
    }, 0);
    return usersCount;
  }, [selectedItemIds]);

  if (initialLoading) {
    return (
      <div className="mt-6">
        <Skeleton />
      </div>
    );
  }

  const renderAddButton = ({
    label = 'Ajouter',
    href,
    onClick = () => {},
    size = 'sm',
    icon
  } = {}) => (
    <Button
      size={size}
      data={{ turbo_frame: 'dialog', turbo_stream: true, turbo_method: 'get' }}
      href={href}
      label={label}
      color="blue"
      onClick={onClick}
      icon={icon}
    />
  );

  const allGroupsClosed = taskItemsGroups.length === closedGroupIds.length;
  const menuIconClasses = 'w-5 h-5';
  const groupsToggler = (
    <button
      className="w-10 h-10 bg-slate-200/50 hover:bg-slate-200/70 rounded-full flex items-center justify-center"
      onClick={() => toggleAllGroups()}
    >
      {allGroupsClosed ? (
        <ChevronUpIcon className={menuIconClasses} />
      ) : (
        <ChevronDownIcon className={menuIconClasses} />
      )}
    </button>
  );

  return (
    <>
      <div className="mt-6 z-[11]">
        <div className="sticky top-6 z-[11]">
          <ToolbarOverlay
            totalFilesCount={totalFilesCount}
            totalRecipientsCount={totalRecipientsCount}
            ids={selectedItemIds}
            onClose={() => setSelectedItemIds([])}
          />
          <ToolbarComponent
            placeholder="Rechercher..."
            classes="z-[1]"
            clear={() => setKeyword('')}
            onChange={e => setKeyword(e.target.value)}
            value={keyword}
          >
            <Filters />
            <div className="flex items-center space-x-2 -mr-4">
              <div className="hidden lg:flex items-center space-x-2">
                {renderAddButton({
                  label: 'Ajouter',
                  onClick: () => setAddModalIsOpened(true),
                  icon: InboxArrowDownIcon
                })}
                {currentTaskItemFilter !== 'INTERNAL' &&
                  renderAddButton({
                    label: 'Importer',
                    href: `/deals/${dealId}/task_items/importer_dialog`,
                    icon: ArrowUpTrayIcon
                  })}
              </div>
              {groupsToggler}
            </div>
          </ToolbarComponent>
        </div>
        {keyword.length > 0 && searchedTaskItemIds.length <= 0 && (
          <div className="bg-white shadow p-6 rounded-lg">
            <Empty title="Aucun résultat" />
          </div>
        )}
        {keyword.length === 0 && sortedGroups.length <= 0 && (
          <div className="bg-white shadow p-6 rounded-lg">
            <Empty title="Aucune démarche">
              {renderAddButton({
                label:
                  currentTaskItemFilter === 'INTERNAL'
                    ? 'Ajouter une démarche interne'
                    : 'Ajouter une démarche',
                size: 'md',
                onClick: () => setAddModalIsOpened(true)
              })}
            </Empty>
          </div>
        )}
        {sortedGroups.length >= 1 && (
          <>
            <ReactSortable
              className="flex flex-col space-y-6 [&:empty]:hidden [&:empty+.empty-state]:block"
              handle=".handle"
              list={sortedGroups}
              setList={newArray => {
                // ui
                const sorted = newArray.map((e, index) => ({ ...e, position: index }));
                setTaskItemsGroups(sorted);

                // backend
                const updatePayload = sorted.map(s => ({ id: s.id, position: s.position }));
                if (sortingTrigger !== JSON.stringify(updatePayload)) {
                  actions.sortGroups(updatePayload);
                }
              }}
              group="groups"
              animation={200}
              delayOnTouchStart={true}
              delay={2}
            >
              {sortedGroups.map(group => {
                return <TaskItemsGroup key={group.id} groupId={group.id} />;
              })}
            </ReactSortable>
            <div className="empty-state hidden bg-white shadow p-6 rounded-lg">
              <Empty title="Aucune démarche" />
            </div>
          </>
        )}
      </div>
      <AddModal isOpen={addModalIsOepned} onClose={() => setAddModalIsOpened(false)} />
    </>
  );
}

export default function Index({ dealId }) {
  return (
    <DealProvider dealId={dealId}>
      <TaskItemsList />
    </DealProvider>
  );
}
